import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Api } from "@noom/noomscape";
import {
  Button,
  CompassColor,
  Input,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import {
  Sponsor,
  SPONSOR_SEARCH_DEFAULT_LIMIT,
  SPONSOR_SEARCH_MIN_QUERY_LENGTH,
} from "@/api/partner/getSponsors";
import { NoomSupportLink } from "@/components/noom-branding";

import axios from "axios";

const SEARCH_DEBOUNCE_MS = 300;

type SearchStatus = "idle" | "loading" | "success" | "rateLimited" | "error";

const STATUS_MESSAGE_KEYS: Partial<Record<SearchStatus, string>> = {
  loading: "loading",
  rateLimited: "rateLimited",
  error: "error",
};

type EmployerDisambiguationInputProps = {
  /** True while a selection is being resolved into a redirect. */
  isLoading?: boolean;
  onSelect: (sponsor: Sponsor) => void;
};

/**
 * "Confirm your provider" typeahead (ENT-2101). Searches sponsors via the
 * ENT-2011 endpoint as the user types and reports the chosen sponsor. The
 * caller is responsible for intersecting the selected `partnerId` with the
 * user's eligibility `candidateMatches` (decoys/non-matches are expected).
 */
export const EmployerDisambiguationInput: React.FC<
  EmployerDisambiguationInputProps
> = ({ isLoading = false, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [status, setStatus] = useState<SearchStatus>("idle");
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = query.trim();

    abortRef.current?.abort();
    setSponsors([]);

    if (trimmed.length < SPONSOR_SEARCH_MIN_QUERY_LENGTH) {
      setStatus("idle");
      return undefined;
    }

    const timeout = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus("loading");
      try {
        const results: Sponsor[] = await Api.call(
          "partner.getSponsors",
          Api.api.partner.getSponsors,
          {
            query: trimmed,
            limit: SPONSOR_SEARCH_DEFAULT_LIMIT,
            signal: controller.signal,
          },
        );
        setSponsors(results);
        setStatus("success");
      } catch (error) {
        if (controller.signal.aborted || axios.isCancel?.(error)) {
          return;
        }
        setSponsors([]);
        setStatus(
          axios.isAxiosError(error) && error.response?.status === 429
            ? "rateLimited"
            : "error",
        );
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
      abortRef.current?.abort();
    };
  }, [query]);

  const showNoResults = status === "success" && sponsors.length === 0;
  const statusMessageKey = STATUS_MESSAGE_KEYS[status];

  return (
    <Stack spacing={Spacing[4]}>
      <Input
        autoFocus
        label={t("eligibilityCheck.multipleMatches.disambiguation.label")}
        placeholder={t(
          "eligibilityCheck.multipleMatches.disambiguation.placeholder",
        )}
        value={query}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setQuery(e.target.value)
        }
      />

      {statusMessageKey && (
        <Text aria-live="polite" size="sm">
          {t(
            `eligibilityCheck.multipleMatches.disambiguation.${statusMessageKey}`,
          )}
        </Text>
      )}

      {sponsors.length > 0 && (
        <Stack spacing={Spacing[2]}>
          {sponsors.map((sponsor) => (
            <Button
              key={sponsor.partnerId}
              size="xl"
              isDisabled={isLoading}
              onClick={() => onSelect(sponsor)}
              backgroundColor={CompassColor.offWhite}
              border={`2px solid ${CompassColor.offWhite}`}
              borderRadius="lg"
              color={CompassColor.black}
              height="60px"
              whiteSpace="normal"
              width="100%"
              maxWidth="100%"
              wordBreak="break-word"
              overflowWrap="break-word"
              _hover={{
                backgroundColor: CompassColor.sand02,
                border: `2px solid ${CompassColor.sand02}`,
              }}
            >
              {sponsor.name}
            </Button>
          ))}
        </Stack>
      )}

      {showNoResults && (
        <Text size="sm">
          {t("eligibilityCheck.multipleMatches.disambiguation.noResults")}
        </Text>
      )}

      <Text size="sm">
        {t("eligibilityCheck.multipleMatches.disambiguation.supportPrompt")}{" "}
        {NoomSupportLink}
      </Text>
    </Stack>
  );
};
